import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, DollarSign, Plus, X, Loader2, Shield, Users, Bell, UserCog } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useStrataConfig, useUpdateStrataConfig } from "@/hooks/useStrataConfig";
import { useUnits } from "@/hooks/useUnits";
import { useAuth } from "@/contexts/AuthContext";
import { usePmPermissions, useUpdatePmPermissions, DEFAULT_PM_PERMISSIONS, type PmPermissions } from "@/hooks/usePmPermissions";

export default function StrataConfig() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: config, isLoading } = useStrataConfig();
  const { data: pmPerms } = usePmPermissions();
  const updatePmPerms = useUpdatePmPermissions();
  const { data: allUnits = [] } = useUnits();
  const updateConfig = useUpdateStrataConfig();

  // ── Bylaw Rules ──────────────────────────────────────────────────
  const [maxNights, setMaxNights] = useState(90);
  const [minStay, setMinStay] = useState(1);
  const [maxGuests, setMaxGuests] = useState(4);
  const [requireInsurance, setRequireInsurance] = useState(true);
  const [approvedUnits, setApprovedUnits] = useState<string[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<string>("");
  const [unitToRemove, setUnitToRemove] = useState<string | null>(null);

  // ── Fees ─────────────────────────────────────────────────────────
  const [feeType, setFeeType] = useState("flat");
  const [feeAmount, setFeeAmount] = useState(125);
  const [collectMunicipal, setCollectMunicipal] = useState(true);
  const [collectProvincial, setCollectProvincial] = useState(true);
  const [mrdtRate, setMrdtRate] = useState(2);      // displayed as %
  const [pstRate, setPstRate] = useState(8);          // displayed as %
  const [strApplicationFee, setStrApplicationFee] = useState(0);

  // ── PM Permissions ────────────────────────────────────────────────
  const [pmPermState, setPmPermState] = useState<PmPermissions>(DEFAULT_PM_PERMISSIONS);

  // ── STR Policy ────────────────────────────────────────────────────
  const [strPolicy, setStrPolicy] = useState<"allow_all" | "principal_residence_only" | "banned">("allow_all");
  const [maxFinePerDay, setMaxFinePerDay] = useState(500);
  const [quietHoursStart, setQuietHoursStart] = useState("22:00");
  const [quietHoursEnd, setQuietHoursEnd] = useState("08:00");
  const [noPartiesEvents, setNoPartiesEvents] = useState(false);

  // ── Guest Policies ────────────────────────────────────────────────
  const [petsAllowedStr, setPetsAllowedStr] = useState(false);
  const [maxVehicles, setMaxVehicles] = useState(1);
  const [maxConsecutiveNights, setMaxConsecutiveNights] = useState<string>("");
  const [minAdvanceBookingDays, setMinAdvanceBookingDays] = useState(0);

  // ── Compliance & Registration ─────────────────────────────────────
  const [requireGoodNeighbour, setRequireGoodNeighbour] = useState(false);
  const [requireCouncilApproval, setRequireCouncilApproval] = useState(false);
  const [requireProvincialReg, setRequireProvincialReg] = useState(true);
  const [requireBusinessLicence, setRequireBusinessLicence] = useState(true);
  const [annualPermitRequired, setAnnualPermitRequired] = useState(true);

  useEffect(() => {
    if (pmPerms) setPmPermState(pmPerms);
  }, [pmPerms]);

  useEffect(() => {
    if (config) {
      setMaxNights(config.max_nights_per_year ?? 90);
      setMinStay(config.min_stay_nights ?? 1);
      setMaxGuests(config.max_guest_count ?? 4);
      setRequireInsurance(config.require_insurance ?? true);
      setApprovedUnits(config.approved_units ?? []);
      setFeeType(config.fee_type ?? "flat");
      setFeeAmount(config.fee_amount ?? 125);
      setCollectMunicipal(config.collect_municipal_tax ?? true);
      setCollectProvincial(config.collect_provincial_tax ?? true);
      setMrdtRate(config.mrdt_rate != null ? config.mrdt_rate * 100 : 2);
      setPstRate(config.pst_rate != null ? config.pst_rate * 100 : 8);
      setStrApplicationFee(config.str_application_fee ?? 0);
      setStrPolicy(config.str_policy ?? "allow_all");
      setMaxFinePerDay(config.max_fine_per_day ?? 500);
      setQuietHoursStart(config.quiet_hours_start ?? "22:00");
      setQuietHoursEnd(config.quiet_hours_end ?? "08:00");
      setNoPartiesEvents(config.no_parties_events ?? false);
      setPetsAllowedStr(config.pets_allowed_str ?? false);
      setMaxVehicles(config.max_vehicles ?? 1);
      setMaxConsecutiveNights(config.max_consecutive_nights != null ? String(config.max_consecutive_nights) : "");
      setMinAdvanceBookingDays(config.min_advance_booking_days ?? 0);
      setRequireGoodNeighbour(config.require_good_neighbour_agreement ?? false);
      setRequireCouncilApproval(config.require_council_approval_per_booking ?? false);
      setRequireProvincialReg(config.require_provincial_registration ?? true);
      setRequireBusinessLicence(config.require_business_licence ?? true);
      setAnnualPermitRequired(config.annual_permit_required ?? true);
    }
  }, [config]);

  const availableToAdd = (allUnits as any[]).filter(
    (u) => !approvedUnits.includes(u.unit_number)
  );

  const handleAddUnit = () => {
    if (!selectedUnit || approvedUnits.includes(selectedUnit)) return;
    setApprovedUnits([...approvedUnits, selectedUnit]);
    setSelectedUnit("");
  };

  const handleRemoveConfirmed = () => {
    if (!unitToRemove) return;
    setApprovedUnits(approvedUnits.filter((u) => u !== unitToRemove));
    setUnitToRemove(null);
  };

  const saveBylaws = () => {
    updateConfig.mutate({
      max_nights_per_year: maxNights,
      min_stay_nights: minStay,
      max_guest_count: maxGuests,
      require_insurance: requireInsurance,
      approved_units: approvedUnits,
    });
  };

  const saveFees = () => {
    updateConfig.mutate({
      fee_type: feeType as any,
      fee_amount: feeAmount,
      collect_municipal_tax: collectMunicipal,
      collect_provincial_tax: collectProvincial,
      mrdt_rate: mrdtRate / 100,
      pst_rate: pstRate / 100,
      str_application_fee: strApplicationFee,
    });
  };

  const savePmPermissions = () => updatePmPerms.mutate(pmPermState);

  const saveBCBylaws = () => {
    updateConfig.mutate({
      str_policy: strPolicy,
      max_fine_per_day: maxFinePerDay,
      quiet_hours_start: quietHoursStart || null,
      quiet_hours_end: quietHoursEnd || null,
      no_parties_events: noPartiesEvents,
      pets_allowed_str: petsAllowedStr,
      max_vehicles: maxVehicles,
      max_consecutive_nights: maxConsecutiveNights ? parseInt(maxConsecutiveNights) : null,
      min_advance_booking_days: minAdvanceBookingDays,
      require_good_neighbour_agreement: requireGoodNeighbour,
      require_council_approval_per_booking: requireCouncilApproval,
      require_provincial_registration: requireProvincialReg,
      require_business_licence: requireBusinessLicence,
      annual_permit_required: annualPermitRequired,
    } as any);
  };

  if (isLoading) {
    return <div className="space-y-6"><Skeleton className="h-8 w-64" /><Skeleton className="h-96" /></div>;
  }

  const strPolicyInfo = {
    allow_all: "All STR permitted — subject to individual host registration and booking rules.",
    principal_residence_only: "Only principal residence STR allowed — owner or tenant must live in the unit.",
    banned: "STR completely banned — requires appropriate vote of strata owners per applicable legislation.",
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Strata Configuration</h1>
        <p className="text-sm text-muted-foreground">Configure bylaws, fees, and STR compliance rules</p>
      </div>

      <Tabs defaultValue="bylaws" className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="bylaws" className="text-xs"><Settings className="mr-1 h-3 w-3" /> Bylaw Rules</TabsTrigger>
          <TabsTrigger value="fees" className="text-xs"><DollarSign className="mr-1 h-3 w-3" /> Fees & Tax</TabsTrigger>
          <TabsTrigger value="bc_bylaws" className="text-xs"><Shield className="mr-1 h-3 w-3" /> STR Policy</TabsTrigger>
          <TabsTrigger value="guest_policies" className="text-xs"><Users className="mr-1 h-3 w-3" /> Guest Policies</TabsTrigger>
          <TabsTrigger value="compliance" className="text-xs"><Bell className="mr-1 h-3 w-3" /> Compliance</TabsTrigger>
          {user?.role === "strata_admin" && (
            <TabsTrigger value="pm_access" className="text-xs"><UserCog className="mr-1 h-3 w-3" /> PM Access</TabsTrigger>
          )}
        </TabsList>

        {/* ══════════════════════════════════════════════════════ */}
        {/* TAB 1: Bylaw Rules                                    */}
        {/* ══════════════════════════════════════════════════════ */}
        <TabsContent value="bylaws" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">STR Bylaw Rules</CardTitle>
              <CardDescription className="text-xs">Set rules automatically enforced on every booking.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs">Maximum nights per calendar year</Label>
                  <Input type="number" value={maxNights} onChange={(e) => setMaxNights(Number(e.target.value))} className="h-9" />
                  <p className="text-[11px] text-muted-foreground">Default: 90 nights/year. Set 0 for no limit.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Minimum stay (nights)</Label>
                  <Input type="number" min="1" value={minStay} onChange={(e) => setMinStay(Number(e.target.value))} className="h-9" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Maximum guest occupancy</Label>
                  <Input type="number" min="1" value={maxGuests} onChange={(e) => setMaxGuests(Number(e.target.value))} className="h-9" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Require insurance certificate</Label>
                  <div className="flex items-center gap-2 pt-1">
                    <Switch checked={requireInsurance} onCheckedChange={setRequireInsurance} />
                    <span className="text-xs text-muted-foreground">Hosts must upload valid insurance</span>
                  </div>
                </div>
              </div>
              <Separator />
              {/* Approved Units */}
              <div className="space-y-3">
                <Label className="text-xs">Units approved for STR</Label>
                {allUnits.length === 0 ? (
                  <div className="rounded-md border border-dashed px-4 py-3 text-center">
                    <p className="text-xs text-muted-foreground">No units registered yet.</p>
                    <Button variant="link" className="h-auto p-0 text-xs mt-1" onClick={() => navigate("/units")}>
                      Go to Unit Management →
                    </Button>
                  </div>
                ) : availableToAdd.length > 0 ? (
                  <div className="flex gap-2">
                    <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                      <SelectTrigger className="h-9 flex-1">
                        <SelectValue placeholder="Select a unit to approve..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableToAdd.map((u: any) => (
                          <SelectItem key={u.id} value={u.unit_number}>
                            Unit {u.unit_number} — {u.owner_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={handleAddUnit} disabled={!selectedUnit}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">All registered units are already approved for STR.</p>
                )}
                {approvedUnits.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {approvedUnits.map((u) => (
                      <Badge key={u} variant="secondary" className="gap-1 text-xs">
                        Unit {u}
                        <button
                          type="button"
                          aria-label={`Remove Unit ${u}`}
                          className="ml-0.5 rounded-full hover:text-destructive"
                          onClick={() => setUnitToRemove(u)}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <Button size="sm" onClick={saveBylaws} disabled={updateConfig.isPending}>
                {updateConfig.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Save Bylaw Rules
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════ */}
        {/* TAB 2: Fees & Tax                                     */}
        {/* ══════════════════════════════════════════════════════ */}
        <TabsContent value="fees">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Fee & Tax Configuration</CardTitle>
              <CardDescription className="text-xs">Configure fees and taxes collected per booking.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs">Strata fee type</Label>
                  <Select value={feeType} onValueChange={setFeeType}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat rate per booking</SelectItem>
                      <SelectItem value="percentage">Percentage of booking</SelectItem>
                      <SelectItem value="per_night">Per night</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Strata fee amount ($)</Label>
                  <Input type="number" min="0" step="0.01" value={feeAmount} onChange={(e) => setFeeAmount(Number(e.target.value))} className="h-9" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">STR Application Fee (per host, one-time) ($)</Label>
                  <Input type="number" min="0" step="0.01" value={strApplicationFee} onChange={(e) => setStrApplicationFee(Number(e.target.value))} className="h-9" />
                  <p className="text-[11px] text-muted-foreground">One-time fee charged when a host registers for STR. Separate from booking fees.</p>
                </div>
              </div>
              <Separator />
              <p className="text-xs font-medium text-muted-foreground">Tax Collection</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium">Municipal & Regional District Tax (MRDT)</p>
                    <p className="text-[11px] text-muted-foreground">Collect and remit to municipality</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number" min="0" max="10" step="0.1"
                      value={mrdtRate}
                      onChange={(e) => setMrdtRate(Number(e.target.value))}
                      className="h-8 w-20 text-xs"
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                    <Switch checked={collectMunicipal} onCheckedChange={setCollectMunicipal} />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium">Provincial Sales Tax (PST)</p>
                    <p className="text-[11px] text-muted-foreground">Provincial sales tax — collect and remit to province</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number" min="0" max="20" step="0.1"
                      value={pstRate}
                      onChange={(e) => setPstRate(Number(e.target.value))}
                      className="h-8 w-20 text-xs"
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                    <Switch checked={collectProvincial} onCheckedChange={setCollectProvincial} />
                  </div>
                </div>
              </div>
              <Button size="sm" onClick={saveFees} disabled={updateConfig.isPending}>
                {updateConfig.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Save Fee Configuration
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════ */}
        {/* TAB 3: BC STR Policy                                  */}
        {/* ══════════════════════════════════════════════════════ */}
        <TabsContent value="bc_bylaws" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">STR Restriction Policy</CardTitle>
              <CardDescription className="text-xs">
                Strata corporations and HOAs may restrict or ban STR by vote of owners per applicable legislation.
                Local government bylaws may impose additional restrictions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs">STR Restriction Policy</Label>
                <Select value={strPolicy} onValueChange={(v) => setStrPolicy(v as any)}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="allow_all">Allow All STR — no restriction beyond registration</SelectItem>
                    <SelectItem value="principal_residence_only">Principal Residence Only — host must live in unit</SelectItem>
                    <SelectItem value="banned">Banned — STR not permitted in this strata (3/4 vote required)</SelectItem>
                  </SelectContent>
                </Select>
                <div className="rounded-md bg-muted px-3 py-2">
                  <p className="text-[11px] text-muted-foreground">{strPolicyInfo[strPolicy]}</p>
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <Label className="text-xs">Maximum fine per day for STR bylaw violation</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm">$</span>
                  <Input
                    type="number" min="0" max="1000" step="50"
                    value={maxFinePerDay}
                    onChange={(e) => setMaxFinePerDay(Math.min(1000, Number(e.target.value)))}
                    className="h-9 w-40"
                  />
                  <span className="text-xs text-muted-foreground">/ day (check your local regulations for maximum)</span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Fine amounts may be subject to jurisdictional limits. Check your local strata regulations.
                </p>
              </div>
              <Separator />
              <div className="space-y-3">
                <Label className="text-xs">Quiet Hours</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quiet_start" className="text-xs">Quiet Hours Start</Label>
                    <Input
                      id="quiet_start"
                      type="time"
                      value={quietHoursStart}
                      onChange={(e) => setQuietHoursStart(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quiet_end" className="text-xs">Quiet Hours End</Label>
                    <Input
                      id="quiet_end"
                      type="time"
                      value={quietHoursEnd}
                      onChange={(e) => setQuietHoursEnd(e.target.value)}
                      className="h-9"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">e.g. 10:00 PM – 8:00 AM. Displayed to guests via the guest portal.</p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium">No Parties or Events</p>
                  <p className="text-[11px] text-muted-foreground">Prohibit use of unit for parties, events, or gatherings</p>
                </div>
                <Switch checked={noPartiesEvents} onCheckedChange={setNoPartiesEvents} />
              </div>
              <Button size="sm" onClick={saveBCBylaws} disabled={updateConfig.isPending}>
                {updateConfig.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Save STR Policy
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════ */}
        {/* TAB 4: Guest Policies                                 */}
        {/* ══════════════════════════════════════════════════════ */}
        <TabsContent value="guest_policies">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Guest & Occupancy Policies</CardTitle>
              <CardDescription className="text-xs">Set rules governing guests, vehicles, and booking windows.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs">Maximum vehicles per booking</Label>
                  <Input
                    type="number" min="0"
                    value={maxVehicles}
                    onChange={(e) => setMaxVehicles(Number(e.target.value))}
                    className="h-9"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Maximum consecutive nights (optional)</Label>
                  <Input
                    type="number" min="1"
                    value={maxConsecutiveNights}
                    onChange={(e) => setMaxConsecutiveNights(e.target.value)}
                    placeholder="No limit"
                    className="h-9"
                  />
                  <p className="text-[11px] text-muted-foreground">Leave blank for no cap on single booking length.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Minimum advance booking notice (days)</Label>
                  <Input
                    type="number" min="0"
                    value={minAdvanceBookingDays}
                    onChange={(e) => setMinAdvanceBookingDays(Number(e.target.value))}
                    className="h-9"
                  />
                  <p className="text-[11px] text-muted-foreground">0 = same-day bookings allowed.</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium">Pets Allowed for STR Guests</p>
                  <p className="text-[11px] text-muted-foreground">Whether STR guests may bring pets into the unit</p>
                </div>
                <Switch checked={petsAllowedStr} onCheckedChange={setPetsAllowedStr} />
              </div>
              <Button size="sm" onClick={saveBCBylaws} disabled={updateConfig.isPending}>
                {updateConfig.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Save Guest Policies
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════ */}
        {/* TAB 5: Compliance & Registration                       */}
        {/* ══════════════════════════════════════════════════════ */}
        <TabsContent value="compliance">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Compliance & Registration Requirements</CardTitle>
              <CardDescription className="text-xs">
                Configure registration and compliance requirements based on your jurisdiction's STR legislation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium">Require Provincial/State STR Registration</p>
                    <p className="text-[11px] text-muted-foreground">
                      Hosts must hold a valid STR registration number issued by the relevant provincial or state authority.
                    </p>
                  </div>
                  <Switch checked={requireProvincialReg} onCheckedChange={setRequireProvincialReg} />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium">Require Local Government Business Licence</p>
                    <p className="text-[11px] text-muted-foreground">
                      A local government business licence may be required as a prerequisite for STR registration in your municipality.
                    </p>
                  </div>
                  <Switch checked={requireBusinessLicence} onCheckedChange={setRequireBusinessLicence} />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium">Annual STR Permit Renewal Required</p>
                    <p className="text-[11px] text-muted-foreground">
                      Hosts must renew their STR registration/permit according to local renewal requirements.
                    </p>
                  </div>
                  <Switch checked={annualPermitRequired} onCheckedChange={setAnnualPermitRequired} />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium">Require Good Neighbour Agreement</p>
                    <p className="text-[11px] text-muted-foreground">
                      Hosts and guests must sign the strata's Good Neighbour Agreement before a booking is approved.
                    </p>
                  </div>
                  <Switch checked={requireGoodNeighbour} onCheckedChange={setRequireGoodNeighbour} />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium">Require Strata Council Approval Per Booking</p>
                    <p className="text-[11px] text-muted-foreground">
                      Every booking requires manual approval by the strata council before it is confirmed.
                    </p>
                  </div>
                  <Switch checked={requireCouncilApproval} onCheckedChange={setRequireCouncilApproval} />
                </div>
              </div>
              <div className="rounded-md bg-muted p-3 text-[11px] text-muted-foreground space-y-1">
                <p className="font-medium text-xs text-foreground">Legislative Reference (BC Example)</p>
                <p>• <strong>Short-Term Rental Accommodations Act</strong> (SBC 2023) — provincial registration, principal residence requirement</p>
                <p>• <strong>Strata Property Act s.119, 126-133, 197</strong> — bylaw adoption and enforcement powers</p>
                <p>• <strong>Strata Property Regulation 7.1 & 7.2</strong> — fines up to $1,000/day for STR bylaw violations</p>
                <p className="italic">Your jurisdiction may have different legislation. Consult local legal counsel.</p>
              </div>
              <Button size="sm" onClick={saveBCBylaws} disabled={updateConfig.isPending}>
                {updateConfig.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Save Compliance Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════ */}
        {/* TAB 6: PM Access (strata_admin only)                  */}
        {/* ══════════════════════════════════════════════════════ */}
        {user?.role === "strata_admin" && (
          <TabsContent value="pm_access">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <UserCog className="h-4 w-4 text-primary" />
                  Property Manager Access Control
                </CardTitle>
                <CardDescription className="text-xs">
                  Choose exactly what your Property Manager can see and do in this building.
                  Toggling a feature off removes it from their sidebar immediately.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1">
                {(
                  [
                    { key: "manage_units",               label: "Units Management",              desc: "View and manage building units and owner information" },
                    { key: "manage_hosts",               label: "Host Management",               desc: "Add, edit and manage STR host registrations" },
                    { key: "manage_bookings",            label: "Booking Management",            desc: "View and manage STR rental bookings" },
                    { key: "manage_compliance",          label: "Compliance Actions",            desc: "Issue compliance notices and take enforcement actions" },
                    { key: "manage_disputes",            label: "Disputes",                      desc: "Open, view and manage disputes" },
                    { key: "manage_fines",               label: "Fines",                          desc: "Issue fines against hosts and resolve disputed fines" },
                    { key: "view_reports",               label: "Reports",                       desc: "Access financial and operational reports" },
                    { key: "manage_amenities",           label: "Amenity Management",            desc: "View the amenities calendar and submit bookings" },
                    { key: "approve_amenity_bookings",   label: "Approve Amenity Bookings",      desc: "Approve or reject pending amenity booking requests from hosts" },
                    { key: "manage_maintenance",         label: "Maintenance",                   desc: "Submit and manage building maintenance requests" },
                    { key: "manage_documents",           label: "Documents",                     desc: "Upload and manage building documents" },
                    { key: "view_audit_log",             label: "Audit Log",                     desc: "View the full organization activity audit log" },
                  ] as { key: keyof PmPermissions; label: string; desc: string }[]
                ).map(({ key, label, desc }, i, arr) => (
                  <div key={key}>
                    <div className="flex items-center justify-between py-2.5">
                      <div>
                        <p className="text-xs font-medium">{label}</p>
                        <p className="text-[11px] text-muted-foreground">{desc}</p>
                      </div>
                      <Switch
                        checked={pmPermState[key]}
                        onCheckedChange={v => setPmPermState(p => ({ ...p, [key]: v }))}
                      />
                    </div>
                    {i < arr.length - 1 && <Separator />}
                  </div>
                ))}
                <div className="pt-3">
                  <Button size="sm" onClick={savePmPermissions} disabled={updatePmPerms.isPending}>
                    {updatePmPerms.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                    Save PM Access Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Remove unit confirmation */}
      <AlertDialog open={!!unitToRemove} onOpenChange={(open) => { if (!open) setUnitToRemove(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Unit {unitToRemove}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove Unit {unitToRemove} from the approved STR units list.
              Hosts in this unit will no longer be able to accept new bookings until re-approved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveConfirmed}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
